import {
  BrowserAuthConfigurationError,
  browserAuthMode,
  currentSupabaseAccessToken,
} from "./supabaseClient";

export interface ApiProblem {
  type: string;
  title: string;
  status: number;
  detail: string;
  code: string;
  correlationId: string | null;
}

export class ApiClientError extends Error {
  readonly problem: ApiProblem;

  constructor(problem: ApiProblem) {
    super(problem.detail);
    this.name = "ApiClientError";
    this.problem = problem;
  }
}

export function resolveApiBaseUrl(
  configuredBaseUrl: string | undefined,
  hostname: string,
): string {
  const hostedFallback =
    hostname === "pipeline.172x.ai" ? "https://api.172x.ai/api/v1" : "/api/v1";
  return (configuredBaseUrl?.trim() || hostedFallback).replace(/\/$/, "");
}

const API_BASE_URL = resolveApiBaseUrl(
  import.meta.env.VITE_API_BASE_URL,
  window.location.hostname,
);
const READ_REQUEST_TIMEOUT_MILLISECONDS = 12_000;

function csrfToken(): string | null {
  const item = document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith("p172x_csrf="));
  return item ? decodeURIComponent(item.split("=", 2)[1] ?? "") : null;
}

function correlationId(): string {
  return crypto.randomUUID();
}

function isMutation(method: string): boolean {
  return !["GET", "HEAD", "OPTIONS"].includes(method);
}

async function parseProblem(response: Response): Promise<ApiProblem> {
  const fallback: ApiProblem = {
    type: "about:blank",
    title: "Request failed",
    status: response.status,
    detail: "172X | Pipeline could not complete this request.",
    code: "request_failed",
    correlationId: response.headers.get("x-correlation-id"),
  };

  try {
    const value = (await response.json()) as Partial<ApiProblem>;
    return {
      ...fallback,
      ...value,
      status: response.status,
      correlationId:
        value.correlationId ?? response.headers.get("x-correlation-id"),
    };
  } catch {
    return fallback;
  }
}

export async function apiRequest<T>(
  path: `/${string}`,
  init: RequestInit = {},
  onResponse?: (headers: Headers) => void,
): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);
  headers.set("accept", "application/json");
  headers.set("x-correlation-id", correlationId());
  if (
    init.body &&
    !(init.body instanceof FormData) &&
    !headers.has("content-type")
  ) {
    headers.set("content-type", "application/json");
  }
  let authMode: "local" | "supabase";
  let accessToken: string | null;
  try {
    authMode = browserAuthMode();
    accessToken = await currentSupabaseAccessToken();
  } catch (error) {
    if (!(error instanceof BrowserAuthConfigurationError)) throw error;
    throw new ApiClientError({
      type: "about:blank",
      title: "Authentication configuration unavailable",
      status: 0,
      detail:
        "172X | Pipeline authentication is not configured for this build.",
      code: "client_auth_configuration_invalid",
      correlationId: headers.get("x-correlation-id"),
    });
  }
  if (accessToken) headers.set("authorization", `Bearer ${accessToken}`);
  if (authMode === "local" && isMutation(method)) {
    const token = csrfToken();
    if (token) headers.set("x-csrf-token", token);
  }

  const requestSignal = timeoutSignal(init.signal, method);
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      method,
      headers,
      credentials: authMode === "local" ? "include" : "omit",
      signal: requestSignal.signal,
    });
  } catch {
    if (requestSignal.timedOut()) {
      throw new ApiClientError({
        type: "about:blank",
        title: "Request timed out",
        status: 0,
        detail:
          "172X | Pipeline took too long to respond. Your data was not assumed saved; retry shortly.",
        code: "request_timeout",
        correlationId: headers.get("x-correlation-id"),
      });
    }
    throw new ApiClientError({
      type: "about:blank",
      title: "Service unavailable",
      status: 0,
      detail:
        "172X | Pipeline is unavailable. Check your connection and retry.",
      code: "network_error",
      correlationId: headers.get("x-correlation-id"),
    });
  } finally {
    requestSignal.dispose();
  }

  if (!response.ok) throw new ApiClientError(await parseProblem(response));
  onResponse?.(response.headers);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

function timeoutSignal(
  supplied: AbortSignal | null | undefined,
  method: string,
): {
  signal: AbortSignal | undefined;
  timedOut: () => boolean;
  dispose: () => void;
} {
  if (isMutation(method)) {
    return {
      signal: supplied ?? undefined,
      timedOut: () => false,
      dispose: () => undefined,
    };
  }
  const controller = new AbortController();
  let didTimeout = false;
  const onAbort = () => controller.abort(supplied?.reason);
  if (supplied?.aborted) onAbort();
  else supplied?.addEventListener("abort", onAbort, { once: true });
  const timer = window.setTimeout(() => {
    didTimeout = true;
    controller.abort();
  }, READ_REQUEST_TIMEOUT_MILLISECONDS);
  return {
    signal: controller.signal,
    timedOut: () => didTimeout,
    dispose: () => {
      window.clearTimeout(timer);
      supplied?.removeEventListener("abort", onAbort);
    },
  };
}

export async function apiDownload(path: `/${string}`): Promise<Blob> {
  const headers = new Headers({
    accept: "application/octet-stream",
    "x-correlation-id": correlationId(),
  });
  let authMode: "local" | "supabase";
  let accessToken: string | null;
  try {
    authMode = browserAuthMode();
    accessToken = await currentSupabaseAccessToken();
  } catch (error) {
    if (!(error instanceof BrowserAuthConfigurationError)) throw error;
    throw new ApiClientError({
      type: "about:blank",
      title: "Authentication configuration unavailable",
      status: 0,
      detail:
        "172X | Pipeline authentication is not configured for this build.",
      code: "client_auth_configuration_invalid",
      correlationId: headers.get("x-correlation-id"),
    });
  }
  if (accessToken) headers.set("authorization", `Bearer ${accessToken}`);
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: "GET",
      headers,
      credentials: authMode === "local" ? "include" : "omit",
    });
  } catch {
    throw new ApiClientError({
      type: "about:blank",
      title: "Artifact unavailable",
      status: 0,
      detail: "The private artifact could not be downloaded. Retry shortly.",
      code: "network_error",
      correlationId: headers.get("x-correlation-id"),
    });
  }
  if (!response.ok) throw new ApiClientError(await parseProblem(response));
  return response.blob();
}
